'use client';

import React from 'react';
import classNames from 'classnames';
import { Caption } from '@/design-system/components/Basic/Typography';

export interface LikertScaleOption {
  label: string;
  value: string | number;
}

export interface LikertScaleProps {
  options: LikertScaleOption[];
  value: string | number | undefined;
  onChange: (value: string | number) => void;
  disabled?: boolean;
  className?: string;
  /** Show labels above dots on desktop (default: false) */
  showDesktopLabels?: boolean;
}

export const LikertScale: React.FC<LikertScaleProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  className,
  showDesktopLabels = false,
}) => {
  const isSelected = (optionValue: string | number) => {
    return String(value) === String(optionValue);
  };

  return (
    <div
      className={classNames('w-full', className, { 'opacity-60 pointer-events-none': disabled })}
    >
      {/* Desktop view - dots with connecting line */}
      <div className="hidden md:block">
        {/* Optional labels above dots */}
        {showDesktopLabels && (
          <div className="flex justify-between mb-2">
            {options.map((option, idx) => (
              <div key={`label-${idx}`} className="flex-1 flex justify-center">
                <Caption
                  weight="bold"
                  className="text-ds-text-3 text-center text-[10px] leading-tight"
                >
                  {option.label}
                </Caption>
              </div>
            ))}
          </div>
        )}

        {/* Dots with connecting line */}
        <div className="relative flex items-center justify-between">
          {/* Connecting line */}
          <div className="absolute top-1/2 left-[10px] right-[10px] h-[1px] bg-ds-grey-300 transform -translate-y-1/2 z-0" />

          {options.map((option, idx) => (
            <button
              key={`dot-${idx}`}
              type="button"
              onClick={() => onChange(option.value)}
              disabled={disabled}
              className={classNames(
                'w-5 h-5 rounded-full transition-all duration-200 relative z-10 cursor-pointer',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-aqua-300 focus-visible:ring-offset-2',
                'active:scale-110',
                {
                  'bg-ds-bg-surface-2': isSelected(option.value),
                  'bg-white border-2 border-ds-grey-300 hover:border-ds-grey-400': !isSelected(
                    option.value,
                  ),
                },
              )}
              aria-label={option.label}
            />
          ))}
        </div>
      </div>

      {/* Mobile view - buttons in a row */}
      <div className="md:hidden flex">
        {options.map((option, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === options.length - 1;

          return (
            <button
              key={`btn-${idx}`}
              type="button"
              onClick={() => onChange(option.value)}
              disabled={disabled}
              className={classNames(
                'flex-1 min-h-[48px] border transition-colors duration-200 cursor-pointer',
                'flex items-center justify-center px-1 py-2',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-aqua-300 focus-visible:ring-inset',
                {
                  // Selected state
                  'bg-ds-bg-surface-2 text-white border-ds-bg-surface-2': isSelected(option.value),
                  // Unselected state
                  'bg-white text-ds-text-3 border-ds-grey-300 hover:border-ds-grey-400':
                    !isSelected(option.value),
                  // Border radius for first and last buttons
                  'rounded-l-lg': isFirst,
                  'rounded-r-lg': isLast,
                  // Remove double borders between buttons
                  '-ml-[1px]': !isFirst,
                },
              )}
            >
              <Caption
                weight="medium"
                className={classNames('text-center leading-tight text-[10px]', {
                  'text-white': isSelected(option.value),
                  'text-ds-text-3': !isSelected(option.value),
                })}
              >
                {option.label}
              </Caption>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LikertScale;
